import React, { forwardRef, useEffect, useRef, useState } from 'react';
import Rodal from '../../../../components/Rodal/Rodal';
import {
    Col,
    FormGroup,
    InputGroupAddon,
    ModalBody,
    ModalHeader,
    Row,
} from 'reactstrap';
import '../NdaTermsPopup.scss';
import { useStateSelector } from '../../../../store/selectors';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import './ExtendDeadlinePopup.scss';
import { EngagementProcess } from '../../../../slices/marketplace/timeline/models/engagement-process.model';
import { LevelOfService, isSfas } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import Helpers from '../../../../utils/helper';
import { LateFeeType } from '../../../../wallet/models/LateFeeType';
import LateFeePaymentComponent, {
    LateFeePaymentDetails,
} from '../../../../components/PaymentDialog/LateFeePaymentComponent/LateFeePaymentComponent';
import { PricesCalculationHelper } from '../../../../utils/pricesCalculationHelper';
import { InvoiceType } from '../../../../models/Invoice/Enums/InvoiceTypeEnum';
import { InvoiceReferenceType } from '../../../../models/Invoice/Enums/InvoiceReferenceTypeEnum';
import { Invoice } from '../../../../models/Invoice/Invoice';
import { PackagePaymentStatus } from '../../../../models/Invoice/Enums/PackagePaymentStatusEnum';
import createNotification from '../../../../utils/createNotification';
import { getBusinessDateStartLocal } from '../../../../slices/marketplace/timeline/utils/timeline-dates';

interface ExtendDeadlinePopupProps {
    isVisible: boolean;
    setIsVisible(value: boolean): void;
    engagementId: number;
    fetchData(): void;
    eventId: string;
    eventDeadline: Date;
    engagementProcess: EngagementProcess;
    levelOfService: LevelOfService;
    mode: 'dataNdaTerms' | 'general';
}

export const ExtendDeadlinePopup = (props: ExtendDeadlinePopupProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [isLoading, setIsLoading] = useState(false);
    const dateRef = useRef(null);
    // The deadline arrives as a midnight-PT instant; render/pick against its PT business
    // date (local midnight), not the viewer-local calendar day of the raw instant.
    const displayDeadline = getBusinessDateStartLocal(props.eventDeadline);
    const [selectedDate, setSelectedDate] = useState<Date>(
        moment(displayDeadline).add(1, 'day').toDate()
    );
    const [isPaymentPopupVisible, setIsPaymentPopupVisible] = useState(false);
    const [lateFeePaymentDetails, setLateFeePaymentDetails] =
        useState<LateFeePaymentDetails>(null);
    const [totalPrice, setTotalPrice] = useState(0);
    const [fteDvmsCount, setFteDvmsCount] = useState(0);
    const [buyersAmount, setBuyersAmount] = useState(0);
    const DateControlInput = forwardRef(({ value, onClick }: any, ref: any) => (
        <div ref={ref} className={'btn'} onClick={onClick}>
            {value}
        </div>
    ));
    const marketplacePricing = useStateSelector(
        (state) => state.marketplacePricing.pricing
    );
    const engagement = useStateSelector(
        (state) => state.timelines.currentTimeline
    );
    const [doesSuccessfulPaymentExist, setDoesSuccessfulPaymentExist] =
        useState(false);

    useEffect(() => {
        getInvoiceExistingStatus();
        const fteDvmsCount = props.engagementProcess?.startFteDvm || 0;

        setFteDvmsCount(fteDvmsCount);

        const buyersLenght =
            engagement?.engagementProcess?.buyers?.filter((f) => f.isActive)
                .length ?? 0;
        setBuyersAmount(buyersLenght);
    }, []);

    useEffect(() => {
        calculateLateFee();
    }, [selectedDate, fteDvmsCount, buyersAmount, doesSuccessfulPaymentExist]);

    const getInvoiceExistingStatus = () => {
        const invoiceRef = {
            invoiceType: InvoiceType.EngagementDeadlineExtensionFee,
            processId: props.engagementId,
            referenceId: props.eventId,
            referenceType: InvoiceReferenceType.EngagementPurchase,
        };
        axios
            .get(
                `api/invoices/invoiceType/${invoiceRef.invoiceType}/process/${invoiceRef.processId}/reference/${invoiceRef.referenceId}/referenceType/${invoiceRef.referenceType}`
            )
            .then((response) => {
                if (response.status == 200) {
                    const isAnySuccessfulPayment =
                        (response.data as Invoice)?.packagePayments?.some(
                            (s) => s.status === PackagePaymentStatus.Confirmed
                        ) ?? false;
                    setDoesSuccessfulPaymentExist(isAnySuccessfulPayment);
                }
            });
    };

    const calculateLateFee = () => {

        // Fractional diff + round so a DST-shortened day still counts as a full day.
        const lateDays = Math.round(
            moment(selectedDate)
                .startOf('day')
                .diff(moment(displayDeadline), 'days', true)
        );
        const levelOfService = props.levelOfService;

        const brokreagePricing =
            PricesCalculationHelper.getBrokeragePricingByLevelOfService(
                levelOfService,
                marketplacePricing
            );
        const scheduleRow = PricesCalculationHelper.getScheduleRow(brokreagePricing.transactionFeeSchedule, props.engagementProcess.startLtmRevenue);
        const firstOccurrencePrice = scheduleRow?.lateFeeFirstOccurrence ?? 0;
        const pricePerDay = scheduleRow?.lateFeeAdditionalOccurrence ?? 0;

        const applyFirstOccurrence = !doesSuccessfulPaymentExist;
        const pricingDetails = PricesCalculationHelper.getLateFeePrice(
            pricePerDay,
            lateDays,
            firstOccurrencePrice,
            applyFirstOccurrence
        );

        setTotalPrice(pricingDetails.totalPrice);
        const lateFeeDetails: LateFeePaymentDetails = {
            lateDays: lateDays,
            feeAmount: pricePerDay,
            totalPrice: pricingDetails.totalPrice,
            firstOccurancePrice: firstOccurrencePrice,
            eventId: props.eventId,
            lateFeeType: LateFeeType.DeadlineExtension,
            fteDvms: fteDvmsCount,
        };
        setLateFeePaymentDetails(lateFeeDetails);
    };

    const handleSuccessfulPayment = () => {
        props.fetchData();
        setIsPaymentPopupVisible(false);
        props.setIsVisible(false);
    };

    const extendDeadlineSFAS = () => {
        setIsLoading(true);
        axios
            .put(
                `/api/Marketplace/Engagements/${props.engagementId}/DeadlineExtension`,
                {
                    engagementId: props.engagementId,
                    eventId: props.eventId,
                    extensionDays: lateFeePaymentDetails.lateDays,
                }
            )
            .then((response) => {
                if (response.status === 200) {
                    createNotification(
                        'Engagement deadline has been successfully extended.',
                        'success'
                    );
                    handleSuccessfulPayment();
                } else {
                    createNotification(
                        response.data?.title ??
                            (response as any)?.message ??
                            'An unexpected error occured. Please try again later',
                        'error'
                    );
                }
            })
            .catch((error) => {
                createNotification(
                    'An unexpected error occured. Please try again later',
                    'error'
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <div className="extend-deadline-popup">
            <Rodal
                visible={props.isVisible}
                onClose={() => props.setIsVisible(false)}
                animation="slideUp"
                className="data-nda-terms-popup extend-deadline-popup"
                showMask={true}
                width={400}
                showCloseButton={true}>
                <ModalHeader>
                    {props.mode === 'dataNdaTerms'
                        ? 'Extend Data NDA Terms Negotiation & Execution Deadline'
                        : "Extend Buyer's Deadline"}
                </ModalHeader>
                <ModalBody>
                    <div className="">
                        {props.mode === 'dataNdaTerms' ? (
                            <Row className="mb-3">
                                {`You have the ability to extend the deadline of the Data NDA terms negotiation${
                                    !isSfas(props.levelOfService)
                                        ? ' by opting to pay late fees.'
                                        : '.'
                                }`}
                            </Row>
                        ) : (
                            []
                        )}
                        <Row className="mb-2">
                            <Col md={4} className="p-0">
                                <label>Current Deadline Date:</label>
                            </Col>
                            <Col md={6} className="p-0">
                                <span>
                                    {moment(displayDeadline).format(
                                        'MM/DD/YYYY'
                                    )}
                                </span>
                            </Col>
                        </Row>
                        <Row className="mb-2">
                            <Col md={4} className="p-0">
                                <label>New Deadline Date:</label>
                            </Col>
                            <Col md={6} className="p-0">
                                <FormGroup className={'d-flex small-border'}>
                                    <InputGroupAddon
                                        addonType="prepend"
                                        onClick={() => {
                                            dateRef.current.setOpen(true);
                                        }}>
                                        <div className="input-group-text">
                                            <FontAwesomeIcon
                                                icon={faCalendarAlt}
                                            />
                                        </div>
                                    </InputGroupAddon>
                                    <DatePicker
                                        portalId="event-start-date-portal"
                                        minDate={moment(displayDeadline)
                                            .add(1, 'day')
                                            .toDate()}
                                        ref={dateRef}
                                        className={'form-control'}
                                        selected={selectedDate}
                                        name="startDate"
                                        popperProps={{ strategy: 'fixed' }}
                                        onChange={(value: Date) => {
                                            setSelectedDate(value);
                                        }}
                                        dateFormat="MM/dd/yyyy"
                                        customInput={<DateControlInput />}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        {!isSfas(props.levelOfService) ? (
                            <Row className="mb-4">
                                <Col md={4} className="p-0">
                                    <label>Late Fees:</label>
                                </Col>
                                <Col md={6} className="p-0">
                                    <span>
                                        {Helpers.GetCurrencyFormat(
                                            totalPrice,
                                            2,
                                            true
                                        )}
                                    </span>
                                </Col>
                            </Row>
                        ) : (
                            []
                        )}
                        <Row>
                            <div className="buttons-wrapper">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => props.setIsVisible(false)}>
                                    Cancel
                                </button>
                                <ButtonLoader
                                    disabled={isLoading}
                                    onClick={() => {
                                        isSfas(props.levelOfService)
                                            ? extendDeadlineSFAS()
                                            : setIsPaymentPopupVisible(true);
                                    }}
                                    className="btn btn-primary"
                                    buttonText={'Extend Deadline'}
                                    isLoading={isLoading}
                                    loaderButtonText={'Extend Deadline'}
                                />
                            </div>
                        </Row>
                    </div>
                </ModalBody>
            </Rodal>

            {isPaymentPopupVisible && (
                <LateFeePaymentComponent
                    isVisible={isPaymentPopupVisible}
                    setIsVisible={setIsPaymentPopupVisible}
                    paymentSuccess={() => {
                        handleSuccessfulPayment();
                    }}
                    engagementId={props.engagementId}
                    eventId={props.eventId}
                    extensionDays={lateFeePaymentDetails.lateDays}
                    feeAmount={lateFeePaymentDetails.feeAmount}
                    totalPrice={lateFeePaymentDetails.totalPrice}
                    lateFeeType={lateFeePaymentDetails.lateFeeType}
                    fteDvms={lateFeePaymentDetails.fteDvms}
                    firstOccurancePrice={
                        lateFeePaymentDetails.firstOccurancePrice
                    }
                />
            )}
        </div>
    );
};
